import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "tipptop:active_group_id";

const ActiveGroupContext = createContext(null);

export function ActiveGroupProvider({ children }) {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGroupId, setActiveGroupIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  const persistActive = useCallback((id) => {
    setActiveGroupIdState(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* private mode */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setGroups([]);
      setLoading(false);
      return [];
    }
    setLoading(true);
    try {
      const { data } = await api.get("/groups/mine");
      if (Array.isArray(data)) {
        setGroups(data);
      } else {
        setGroups([]);
        return [];
      }
      const stored = (() => {
        try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
      })();
      const exists = stored && data.some((g) => g.group_id === stored);
      if (exists) {
        setActiveGroupIdState(stored);
      } else if (data.length > 0) {
        persistActive(data[0].group_id);
      } else {
        persistActive(null);
      }
      return data;
    } finally {
      setLoading(false);
    }
  }, [user, persistActive]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setActiveGroupId = useCallback((id) => {
    persistActive(id);
  }, [persistActive]);

  const activeGroup = useMemo(
    () => (Array.isArray(groups) ? groups.find((g) => g.group_id === activeGroupId) : null),
    [groups, activeGroupId],
  );

  const value = useMemo(
    () => ({ groups, activeGroup, activeGroupId, setActiveGroupId, refresh, loading }),
    [groups, activeGroup, activeGroupId, setActiveGroupId, refresh, loading],
  );

  return <ActiveGroupContext.Provider value={value}>{children}</ActiveGroupContext.Provider>;
}

export function useActiveGroup() {
  const ctx = useContext(ActiveGroupContext);
  if (!ctx) throw new Error("useActiveGroup must be used within ActiveGroupProvider");
  return ctx;
}
